import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";

const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    width: "480px",
    maxWidth: "90vw",
    backgroundColor: "#F7F8FB",
    padding: "8px",
  },
});

const ModalHeader = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 24px 8px 24px",
});

const ModalContent = styled(DialogContent)({
  padding: "8px 24px 20px 24px",
});

const ModalActions = styled(DialogActions)({
  padding: "8px 24px 16px 24px",
  gap: "12px",
});

const PapSavingsModal = ({ open, onClose, opportunity, onSave }) => {
  const [planSavings, setPlanSavings] = useState("");
  const [memberSavings, setMemberSavings] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open && opportunity) {
      // Pre-populate with existing values if available
      setPlanSavings(
        opportunity.planSavings !== undefined &&
          opportunity.planSavings !== null
          ? String(opportunity.planSavings)
          : "",
      );
      setMemberSavings(
        opportunity.memberSavings !== undefined &&
          opportunity.memberSavings !== null
          ? String(opportunity.memberSavings)
          : "",
      );
      setErrors({});
    }
  }, [open, opportunity]);

  const validateCurrency = (val) => {
    if (val === "") return true;
    // NUMBER(19,2): max 17 digits before decimal, max 2 decimal places
    const regex = /^\d{1,17}(\.\d{0,2})?$/;
    return regex.test(val);
  };

  const isValidFormat = (val) => {
    if (val === "") return true;
    return /^\d{1,17}(\.\d{1,2})?$/.test(val) || /^\d{1,17}$/.test(val);
  };

  const handlePlanSavingsChange = (e) => {
    const val = e.target.value;
    if (val === "" || validateCurrency(val)) {
      setPlanSavings(val);
      setErrors((prev) => ({ ...prev, planSavings: "" }));
    }
  };

  const handleMemberSavingsChange = (e) => {
    const val = e.target.value;
    if (val === "" || validateCurrency(val)) {
      setMemberSavings(val);
      setErrors((prev) => ({ ...prev, memberSavings: "" }));
    }
  };

  const handleSave = async () => {
    const newErrors = {};
    if (planSavings !== "" && !isValidFormat(planSavings)) {
      newErrors.planSavings =
        "Invalid value. Maximum 17 digits before decimal and 2 decimal places allowed.";
    }
    if (memberSavings !== "" && !isValidFormat(memberSavings)) {
      newErrors.memberSavings =
        "Invalid value. Maximum 17 digits before decimal and 2 decimal places allowed.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSaving(true);
    try {
      const planVal =
        planSavings === "" ? null : Number.parseFloat(planSavings);
      const memberVal =
        memberSavings === "" ? null : Number.parseFloat(memberSavings);
      await onSave(planVal, memberVal);
      onClose();
    } catch (error) {
      console.error("Failed to save PAP savings:", error);
      setErrors((prev) => ({
        ...prev,
        submit: error.message || "Failed to save savings. Please try again.",
      }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      aria-labelledby="pap-savings-modal-title"
    >
      <ModalHeader>
        <Typography
          id="pap-savings-modal-title"
          variant="h6"
          sx={{ fontWeight: 700, color: "#403C6F", fontSize: "1.1rem" }}
        >
          PAP Savings
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          disabled={isSaving}
          aria-label="Close modal"
        >
          <CloseIcon sx={{ color: "#8E8D9F" }} />
        </IconButton>
      </ModalHeader>

      <ModalContent>
        {opportunity && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              border: "1px solid #E8E8E9",
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#1B2F3F" }}
            >
              {opportunity.drug || "Unknown Drug"}
              {opportunity.strength && (
                <span
                  style={{
                    marginLeft: "6px",
                    fontWeight: 500,
                    color: "#66638C",
                    whiteSpace: "nowrap",
                  }}
                >
                  | {opportunity.strength}
                </span>
              )}
            </Typography>
            {opportunity.cost && (
              <Typography
                variant="caption"
                sx={{ color: "#6C757D", display: "block", mt: 0.5 }}
              >
                Member Cost: {opportunity.cost}
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
            >
              Total PAP Value
            </Typography>
            <TextField
              variant="outlined"
              size="small"
              value={planSavings}
              onChange={handlePlanSavingsChange}
              disabled={isSaving}
              error={!!errors.planSavings}
              helperText={errors.planSavings}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: errors.planSavings ? "#d32f2f" : "#E8E8E9",
                  },
                  "&:hover fieldset": {
                    borderColor: "#403C6F",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#403C6F",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  py: 1.5,
                  color: "#0e1324",
                },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "13px", color: "#66638C", fontWeight: 500 }}
            >
              Member Copay × # of fills
            </Typography>
            <TextField
              variant="outlined"
              size="small"
              value={memberSavings}
              onChange={handleMemberSavingsChange}
              disabled={isSaving}
              error={!!errors.memberSavings}
              helperText={errors.memberSavings}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: errors.memberSavings ? "#d32f2f" : "#E8E8E9",
                  },
                  "&:hover fieldset": {
                    borderColor: "#403C6F",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#403C6F",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  py: 1.5,
                  color: "#0e1324",
                },
              }}
            />
          </Box>
        </Box>

        {errors.submit && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", mt: 2, textAlign: "center" }}
          >
            {errors.submit}
          </Typography>
        )}
      </ModalContent>

      <ModalActions>
        <Button
          onClick={onClose}
          disabled={isSaving}
          sx={{
            color: "#6E6D92",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { backgroundColor: "rgba(110, 109, 146, 0.08)" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          variant="contained"
          sx={{
            backgroundColor: "#403C6F",
            color: "#FFFFFF",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "6px",
            px: 3,
            "&:hover": {
              backgroundColor: "#312e56",
            },
            minWidth: "100px",
          }}
        >
          {isSaving ? <CircularProgress size={20} color="inherit" /> : "Save"}
        </Button>
      </ModalActions>
    </StyledDialog>
  );
};

PapSavingsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  opportunity: PropTypes.object,
  onSave: PropTypes.func.isRequired,
};

export default PapSavingsModal;
